import type { EstimateLineItem, Part } from '../types';

/** A common part for a repeat service, as returned by the knowledge-base API. */
export interface KBPart {
  part_number: string;
  description: string;
  avg_unit_price: number;
  avg_quantity: number;
  frequency: number;
}

/** A repeat-match service recommendation from the knowledge-base API. */
export interface KBSuggestion {
  description: string;
  avg_labor_hours: number;
  avg_hourly_rate: number;
  avg_total: number;
  count: number;
  parts?: KBPart[];
  last_notes?: string | null;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/**
 * Turn a repeat-match recommendation into a ready-to-save estimate line item —
 * the GarageBoss-style one-tap autofill. Fills labor hours, labor rate, the
 * common parts (rounded), the last tech note (cause/concern), and a consistent
 * total computed from labor + parts. Marked `pending` so the tech confirms
 * before it counts as priced work.
 */
export function buildLineItemFromSuggestion(
  suggestion: KBSuggestion,
  defaultLaborRate: number
): EstimateLineItem {
  const laborHours = suggestion.avg_labor_hours || 0;
  const hourlyRate =
    suggestion.avg_hourly_rate && suggestion.avg_hourly_rate > 0
      ? round2(suggestion.avg_hourly_rate)
      : defaultLaborRate;

  const parts: Part[] = (suggestion.parts ?? []).map((p) => {
    const quantity = Math.max(1, Math.round(p.avg_quantity || 1));
    const unitPrice = round2(p.avg_unit_price || 0);
    return {
      part_number: p.part_number,
      description: p.description,
      quantity,
      unit_price: unitPrice,
      total_price: round2(quantity * unitPrice),
      availability: 'order_required',
    };
  });

  const laborCost = round2(laborHours * hourlyRate);
  const partsCost = round2(parts.reduce((sum, p) => sum + p.total_price, 0));

  return {
    description: suggestion.description,
    category: 'Custom',
    quantity: 1,
    labor_hours: laborHours,
    hourly_rate: hourlyRate,
    total_price: round2(laborCost + partsCost),
    approval_status: 'pending',
    parts,
    ...(suggestion.last_notes ? { notes: suggestion.last_notes } : {}),
  } as EstimateLineItem;
}
